import React, { useLayoutEffect, useRef } from 'react';
import { Pagination as BootstrapPagination } from 'react-bootstrap';

import {
  ELLIPSIS,
  buildPageWindow,
  formatInteger,
  formatRangeLabel,
  getPageCount,
} from 'components/DesignSystem/Pagination/pageWindow';

import './index.scss';

export const DEFAULT_PER_PAGE_OPTIONS: readonly number[] = [10, 20, 50];

export interface PaginationProps {
  /**
   * Página atual (base 1). O consumidor mantém o valor em [1, número de páginas]: o
   * componente não corrige uma página fora do intervalo (ex.: ao trocar de 10 para 50
   * por página estando na página 100, o consumidor volta para a última página válida).
   */
  page: number;
  /** Itens por página. */
  perPage: number;
  /** Total de itens (não de páginas). */
  total: number;
  /** Opções do "Por página". Padrão: 10, 20 e 50. */
  perPageOptions?: readonly number[];
  /** Chamado com a página escolhida (número, ‹ ou ›). */
  onPageChange: (page: number) => void;
  /** Chamado com a nova quantidade por página; não dispara para o valor atual. */
  onPerPageChange: (perPage: number) => void;
  /**
   * Bloqueia a troca de página e de "Por página" (ex.: enquanto a lista carrega). Páginas,
   * setas e botões do "Por página" ficam com `aria-disabled` e o visual de desabilitados, mas
   * continuam montados e focáveis (o foco não se perde no meio do carregamento) e ignoram
   * clique, Enter e Espaço.
   */
  disabled?: boolean;
  /** Complemento do rótulo, após " · " (ex.: "ordenado por última execução"). */
  trailingLabel?: string;
  className?: string;
}

/**
 * Paginação numerada do Design System, só de apresentação (não conhece API).
 *
 * Rótulo "Mostrando a–b de N" à esquerda, janela de páginas no centro (sobre o
 * `Pagination` do react-bootstrap) e "Por página" à direita, em botões soltos.
 * O `nav` leva "Página X de Y" como `aria-label` (D21) e a página ativa leva
 * `aria-current="page"`.
 *
 * Foco: o `PageItem` troca o elemento clicável por `<span>` quando o item fica ativo ou
 * `disabled`, e o React desmonta o elemento focado. Por isso as páginas e setas são `<button>`
 * (`as="button"`: o `<a>` padrão, do `Anchor` do @restart/ui, descarta o `aria-disabled`), o
 * `disabled` de carregamento não vira o `disabled` do `PageItem`, e, quando a troca de página
 * parte de um item focado que some (vira a ativa ou a seta chega no extremo), o foco volta
 * para a página ativa.
 */
export default function Pagination({
  page,
  perPage,
  total,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  onPageChange,
  onPerPageChange,
  disabled = false,
  trailingLabel,
  className = '',
}: PaginationProps) {
  const pagesN = getPageCount(total, perPage);
  const pageWindow = buildPageWindow(page, pagesN);
  const atFirstPage = page <= 1;
  const atLastPage = page >= pagesN;
  const rangeLabel = formatRangeLabel(page, perPage, total);
  // Carregando: o item continua <button> focável, só com a classe e o aria-disabled.
  const loadingClassName = disabled ? 'disabled' : undefined;

  const navRef = useRef<HTMLElement>(null);
  // Página pedida por um item focado dentro do `nav`; null quando o pedido veio de fora.
  const focusTargetRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const target = focusTargetRef.current;
    focusTargetRef.current = null;
    // Só a troca pedida pelo nav devolve o foco: se o consumidor recusou o pedido e a página
    // mudou depois por outro motivo (ex.: um filtro voltou para a 1), não mexe.
    if (target !== page) return;
    const nav = navRef.current;
    const { activeElement, body } = document;
    // Só devolve se o foco se perdeu (o item desmontou e o foco caiu no body); se ele segue
    // num item do `nav` ou o consumidor o levou para outro lugar, não mexe.
    if (!nav || (activeElement && activeElement !== body)) return;
    nav.querySelector<HTMLElement>('[aria-current="page"]')?.focus();
  }, [page]);

  function changePage(target: number) {
    if (disabled) return;
    focusTargetRef.current = navRef.current?.contains(document.activeElement) ? target : null;
    onPageChange(target);
  }

  return (
    <div className={`ds-pagination ${className}`.trim()}>
      <span className="ds-pagination-range">
        {trailingLabel ? `${rangeLabel} · ${trailingLabel}` : rangeLabel}
      </span>

      <nav
        ref={navRef}
        className="ds-pagination-pages"
        aria-label={`Página ${formatInteger(page)} de ${formatInteger(pagesN)}`}
      >
        <BootstrapPagination>
          {/* No extremo vira <span> (fora da ordem de Tab); o nome vem do visually-hidden. */}
          <BootstrapPagination.Item
            as="button"
            type={atFirstPage ? undefined : 'button'}
            className={atFirstPage ? undefined : loadingClassName}
            disabled={atFirstPage}
            aria-disabled={atFirstPage || disabled || undefined}
            onClick={atFirstPage ? undefined : () => changePage(page - 1)}
          >
            <span aria-hidden="true">‹</span>
            <span className="visually-hidden">Página anterior</span>
          </BootstrapPagination.Item>

          {pageWindow.map((item, index) => {
            if (item === ELLIPSIS) {
              return (
                <BootstrapPagination.Item
                  key={`ellipsis-after-${pageWindow[index - 1]}`}
                  className="ds-pagination-ellipsis"
                  disabled
                >
                  <span aria-hidden="true">…</span>
                  <span className="visually-hidden">páginas omitidas</span>
                </BootstrapPagination.Item>
              );
            }

            const active = item === page;
            // A ativa já é <span> no react-bootstrap e mantém o destaque mesmo carregando;
            // `tabIndex=-1` a deixa receber o foco devolvido sem entrar na ordem de Tab.
            return (
              <BootstrapPagination.Item
                key={`page-${item}`}
                as="button"
                type={active ? undefined : 'button'}
                active={active}
                activeLabel=""
                className={active ? undefined : loadingClassName}
                aria-current={active ? 'page' : undefined}
                aria-disabled={(!active && disabled) || undefined}
                tabIndex={active ? -1 : undefined}
                onClick={active ? undefined : () => changePage(item)}
              >
                {formatInteger(item)}
              </BootstrapPagination.Item>
            );
          })}

          <BootstrapPagination.Item
            as="button"
            type={atLastPage ? undefined : 'button'}
            className={atLastPage ? undefined : loadingClassName}
            disabled={atLastPage}
            aria-disabled={atLastPage || disabled || undefined}
            onClick={atLastPage ? undefined : () => changePage(page + 1)}
          >
            <span aria-hidden="true">›</span>
            <span className="visually-hidden">Próxima página</span>
          </BootstrapPagination.Item>
        </BootstrapPagination>
      </nav>

      <div className="ds-pagination-per-page" role="group" aria-label="Itens por página">
        <span>Por página</span>
        {perPageOptions.map((option) => {
          const active = option === perPage;
          return (
            <button
              key={option}
              type="button"
              className={`ds-pagination-per-page-option${active ? ' active' : ''}`}
              aria-pressed={active}
              // Como nas páginas: carregando, o botão segue focável (aria-disabled, não disabled).
              aria-disabled={disabled || undefined}
              onClick={active || disabled ? undefined : () => onPerPageChange(option)}
            >
              {formatInteger(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
